import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { useDialogModalState } from "../../../src/hook/handle-modal/hooks/dialog-modal-state";
import { useDialogModal } from "../../../src/hook/handle-modal/hooks/actions";
import { useEffect, useRef, type WheelEvent } from "react";

/**
 * Encontra, a partir do alvo do wheel até o limite (o container de scroll do
 * modal), o elemento scrollável mais próximo que ainda tem espaço para rolar na
 * direção do gesto. Usado para reaplicar o scroll manualmente: o Radix Dialog
 * (modal) usa react-remove-scroll, que cancela o scroll NATIVO do wheel dentro
 * do modal — mas não impede um handler onWheel de rolar na mão.
 */
function findScrollableTarget(
  start: EventTarget | null,
  boundary: HTMLElement,
  deltaY: number,
): HTMLElement {
  let node = start instanceof HTMLElement ? start : boundary;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight;
    if (canScroll) {
      const atTop = node.scrollTop <= 0;
      const atBottom =
        node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
      if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return node;
    }
    if (node === boundary || !node.parentElement) break;
    node = node.parentElement;
  }
  return boundary;
}

export function Modal() {
  const { isOpen, element, title, description, onClose } = useDialogModalState();
  const { handleModal } = useDialogModal();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    handleModal({
      isOpen: false,
      element: null,
      title: "",
      description: "",
    });
  };

  useEffect(() => {
    if (!isOpen && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      // modal={false}
    >
      <DialogContent className="rounded-md w-fit min-w-[400px] max-w-[95vw] bg-white transition-none max-h-[90vh] overflow-hidden flex flex-col pb-4"
        onPointerDownOutside={(e) => {
          if (e.target instanceof Node && document.querySelector('[role="dialog"]')?.contains(e.target)) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="w-full flex-shrink-0 border-b pb-5">
          {title ? (
            typeof title === "string" ? (
              <DialogTitle className="text-2xl font-bold break-words text-[#2E0D1E]">
                {title}
              </DialogTitle>
            ) : (
              title
            )
          ) : (
            <DialogTitle className="text-2xl font-bold break-words text-[#2E0D1E]">
              {" "}
            </DialogTitle>
          )}
          {description && (
            typeof description === "string" ? (
              <DialogDescription className="text-muted-foreground break-words">
                {description}
              </DialogDescription>
            ) : (
              description
            )
          )}
        </DialogHeader>
        <div className="flex flex-col items-center w-full flex-1 min-h-0">
          {!element ? (
            <div className="flex w-full items-center justify-center">
              <p>Carregando...</p>
            </div>
          ) : (
            <div
              className="w-full h-full overflow-y-auto"
              onWheel={(e: WheelEvent<HTMLDivElement>) => {
                const scroller = findScrollableTarget(
                  e.target,
                  e.currentTarget,
                  e.deltaY,
                );
                scroller.scrollTop += e.deltaY;
              }}
            >
              {element}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog> 
  );
}
